<script lang="ts" setup generic="TItem extends ID | ID[]">
import GwBtn, { type BtnProps } from '@/components/lib/GwBtn/GwBtn.vue'
import GwBox from '@/components/lib/GwBox/GwBox.vue'
import { isActive } from '@/components/lib/GwBtnGroup/isGwBtnGroupActive'
import type { ID } from '../../../types/basic'
import type { BoxVariants } from '../GwBox/gwBoxVariants'

export type GwBtnGroupItem<T extends ID | ID[] = ID> = {
  id: T
  text?: string
  btnProps?: BtnProps
}

export type GwBtnGroupProps<T extends ID | ID[] = ID> = {
  direction?: 'horizontal' | 'vertical'
  boxVariant: BoxVariants
  tstPrefix?: string
  btnProps?: BtnProps
  btnVariant?: BtnProps['variant']
  options: GwBtnGroupItem<T>[]
} & (
  | {
      isSingle?: true
      value?: T | null
    }
  | {
      isSingle: false
      value?: T | T[] | null
    }
)

const props = withDefaults(defineProps<GwBtnGroupProps<TItem>>(), {
  direction: 'vertical',
  btnVariant: 'btn_text_grey_white',
  isSingle: true,
})

const emit = defineEmits<{
  click: [e: MouseEvent, item: GwBtnGroupItem<TItem>]
  'update:value': [value: any]
}>()

const handleClick = (e: MouseEvent, item: any) => {
  emit('click', e, item)

  if (props.isSingle) {
    emit('update:value', props.value === item.id ? null : item.id)
  } else {
    let _value = Array.isArray(props.value) ? props.value : []
    _value = _value.includes(item.id) ? _value.filter(id => id !== item.id) : [..._value, item.id]
    emit('update:value', _value)
  }
}
</script>

<template>
  <GwBox
    :variant="boxVariant"
    class="gwbtngroup"
    :class="`gwbtngroup_direction_${direction}`"
  >
    <slot name="before"></slot>
    <GwBtn
      v-for="item in options"
      :key="Array.isArray(item.id) ? item.id.join('_') : item.id"
      :data-tst="`${tstPrefix}_${item.id}`"
      :active="isActive(item.id, props.isSingle, props.value as TItem)"
      :variant="btnVariant"
      class="gwbtngroup_item"
      v-bind="{ ...btnProps, ...(item.btnProps || {}) }"
      @click="handleClick($event, item)"
    >
      <slot :item="item">
        {{ item.text }}
      </slot>
    </GwBtn>
    <slot name="after"></slot>
  </GwBox>
</template>

<style lang="scss" scoped>
.gwbtngroup_direction_vertical {
  .gwbtngroup_item {
    min-width: 100%;
  }
}
</style>
